import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms'; // For ngModel
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../../shared-services/config.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ActivatedRoute, Router } from '@angular/router';

// Import Angular Material modules
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Subscription } from 'rxjs';

const DATE_DISPLAY_FORMATS = {
  parse: { dateInput: null },
  display: {
    dateInput: { month: '2-digit', day: '2-digit', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { month: 'long', day: 'numeric', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' },
  },
};

@Component({
  selector: 'app-side-entity-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: DATE_DISPLAY_FORMATS },
  ],
  templateUrl: './side-entity-form.component.html',
  styleUrls: ['./side-entity-form.component.scss'] // Use styleUrls
})
export class SideEntityFormComponent implements OnInit, OnDestroy {
  @Input() data:any;
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly hostElementRef: ElementRef<HTMLElement> = inject(ElementRef);

  get componentColourStyles(): Record<string, string> {
    const palette = Array.isArray(this.data?.themeOverrides?.chart?.palette)
      ? this.data.themeOverrides.chart.palette
      : [];
    const primaryDefault = palette[0];
    const primaryDark = palette[1] || palette[0];
    const resetColour = palette[2];

    const styles: Record<string, string> = {
      '--side-entity-form-primary-text': '#ffffff',
    };

    if (primaryDefault) {
      styles['--side-entity-form-primary-default'] = primaryDefault;
    }

    if (primaryDark) {
      styles['--side-entity-form-primary-dark'] = primaryDark;
    }

    if (resetColour) {
      styles['--side-entity-form-reset-bg'] = resetColour;
    }

    return styles;
  }

  get containerHeight(): string {
    const configured = this.data?.height || this.data?.chartConfig?.height;
    if (typeof configured === 'number') {
      return `${configured}px`;
    }
    if (typeof configured === 'string' && configured.trim()) {
      const normalized = configured.trim().toLowerCase();
      if (normalized.endsWith('vh') || normalized.endsWith('vw') || normalized.endsWith('%')) {
        return '100%';
      }
      return configured;
    }
    return '100%';
  }

  // Local copy of the record for editing
  editableRecord: any = null;
  originalRecord: any = null;
  formFields: { key: string, label: string }[] = [];
  fieldDropdownOptions: { [key: string]: any[] } = {};
  fieldDropdownLoading: { [key: string]: boolean } = {};
  fieldDropdownSearchTerms: { [key: string]: string } = {};
  sectionTitle: string = 'Detials'; // Default title
  selectedEntityValue: any = null;
  isSaving: boolean = false;
  loading: boolean = false;


  private recordFilterSubscription!: Subscription;
  private dropdownFilterSubscription!: Subscription;
  private loadingSubscription!: Subscription;
  private domainAttributesSubscription?: Subscription;
  private reflowSub?: Subscription;
  private domainAttributeKeys = new Set<string>();
  private freeformFieldKeys = new Set<string>();
  private domainAttributeEntities = new Map<string, string>();
  private loadedDomainAttributesEntity: string | null = null;
  private tableSchemaFields: { key: string, label: string }[] = [];
  private suppressEmptyRecordTemplate: boolean = false;


  constructor(
    private globalFilterService: GlobalFilterService,
    private http: HttpClient,
    private configService: ConfigService,
    private snackBar: MatSnackBar,
    private responsiveLayoutService: ResponsiveLayoutService,
    private cdr: ChangeDetectorRef) {

  }

  private cloneRecord<T>(record: T): T {
    if (record === null || record === undefined || typeof record !== 'object') {
      return record;
    }

    return JSON.parse(JSON.stringify(record));
  }

  private createEmptyRecordFromFields(fields: { key: string, label: string }[]): Record<string, any> {
    return fields.reduce((acc, field) => {
      acc[field.key] = null;
      return acc;
    }, {} as Record<string, any>);
  }

  ngOnInit(): void {
    const recordFilterListeners: string[] = this.data?.filterListner || [];
    if (recordFilterListeners.length > 0) {
      this.recordFilterSubscription = this.globalFilterService.filter$.subscribe(filterData => {
        const changedKeys = Array.isArray(filterData.changedFilterKey)
          ? filterData.changedFilterKey
          : [filterData.changedFilterKey];
        const matchingKey = changedKeys.find((key: any) => recordFilterListeners.includes(key));
        const matchingSchemaKey = changedKeys.find((key: any) => this.getColumnSchemaFilterKeys().includes(String(key)));

        if (matchingSchemaKey) {
          this.tableSchemaFields = this.normalizeConfiguredFieldList(filterData.requestParams?.[matchingSchemaKey]);
          this.generateFormFieldsFromRecord();
        }

        if (matchingKey) {
          const selectedRecord = filterData.requestParams?.[matchingKey];
          if (selectedRecord && selectedRecord.length > 0) {
            this.suppressEmptyRecordTemplate = false;
            this.originalRecord = this.cloneRecord(selectedRecord[0]);
            this.editableRecord = this.cloneRecord(this.originalRecord);
            this.generateFormFieldsFromRecord();
            const titleFromRecord = this.getCurrentSelectedEntityTitle() || this.deriveSectionTitleFromRecord(this.originalRecord);
            if (titleFromRecord) {
              this.sectionTitle = titleFromRecord;
            }
          } else {
            this.clearSelectedRecordState(true);
          }
        }
      });
    }

    this.loadingSubscription = this.globalFilterService.filter$.subscribe(filterData => {
      const loadingFilterListeners: string[] =
        this.data?.loadingFilterListner ||
        this.data?.loadingFilterListener ||
        recordFilterListeners;
      const shouldReadLoading =
        this.filterKeyMatches(filterData.changedFilterKey, 'loading') &&
        (
          loadingFilterListeners.length === 0 ||
          loadingFilterListeners.includes(filterData.widgetId)
        );

      if (shouldReadLoading) {
        const loadingState = filterData.requestParams['loading'];
        if (loadingState && loadingState.length > 0) {
          this.loading = loadingState[0];
        }
      }
    });

    // Subscription for dynamic section title from dropdown in entity-management
    this.dropdownFilterSubscription = this.globalFilterService.filter$.subscribe(filterData => {
      const entityFilterKey = this.getEntityFilterKey();
      const selectedEntityLabelFilterKey = this.getSelectedEntityLabelFilterKey();
      if (this.filterKeyMatches(filterData.changedFilterKey, selectedEntityLabelFilterKey)) {
        const label = this.getFirstValue(
          filterData.queryParams?.[selectedEntityLabelFilterKey],
          filterData.requestParams?.[selectedEntityLabelFilterKey],
        );
        if (label) {
          this.sectionTitle = label;
        }
      } else if (this.filterKeyMatches(filterData.changedFilterKey, entityFilterKey)) {
        const selectedEntity = this.getFirstValue(
          filterData.queryParams?.[entityFilterKey],
          filterData.requestParams?.[entityFilterKey],
        );
        if (selectedEntity) {
          this.selectedEntityValue = selectedEntity;
          this.clearSelectedRecordState(true);
          this.loadDomainAttributesForEntity(selectedEntity);
          const storedLabel = this.getFirstValue(
            filterData.queryParams?.[selectedEntityLabelFilterKey],
            filterData.requestParams?.[selectedEntityLabelFilterKey],
          );
          this.sectionTitle = storedLabel || selectedEntity;
        }
      }
    });

    // Initial record if provided
    if (this.data?.record) {
      this.originalRecord = this.cloneRecord(this.data.record);
      this.editableRecord = this.cloneRecord(this.originalRecord);
      this.generateFormFieldsFromRecord();
    } else {
      this.generateFormFieldsFromRecord();
    }
    
    // Set initial title from config if available, otherwise default
    if (this.data?.sections?.[0]?.title) {
      this.sectionTitle = this.data.sections[0].title;
    }

    // Override with the current entity label already in the filter service (handles page load order)
    const currentFilters = this.globalFilterService.getCurrentFilters?.();
    const initialTableSchema = this.getColumnSchemaFilterKeys()
      .flatMap((key) => currentFilters?.requestParams?.[key] || [])
      .filter(Boolean);
    if (initialTableSchema.length > 0) {
      this.tableSchemaFields = this.normalizeConfiguredFieldList(initialTableSchema);
    }
    const entityFilterKey = this.getEntityFilterKey();
    const selectedEntityLabelFilterKey = this.getSelectedEntityLabelFilterKey();
    const initialEntity = this.normalizeFilterValue(
      currentFilters?.queryParams?.[entityFilterKey] ||
      currentFilters?.requestParams?.[entityFilterKey]
    );
    const initialLabel = this.normalizeFilterValue(
      currentFilters?.queryParams?.[selectedEntityLabelFilterKey] ||
      currentFilters?.requestParams?.[selectedEntityLabelFilterKey]
    );
    if (initialLabel) {
      this.sectionTitle = initialLabel;
    } else {
      if (initialEntity) {
        this.sectionTitle = initialEntity;
      }
    }
    if (initialEntity) {
      this.selectedEntityValue = initialEntity;
      this.loadDomainAttributesForEntity(initialEntity);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.cdr.detectChanges();
    });
  }
  
  protected generateFormFieldsFromRecord(): void {
    const excludedColumns = this.getExcludedColumns();
    const recordFields = this.editableRecord
      ? Object.keys(this.editableRecord)
          .filter((key) => key !== 'id' && !excludedColumns.has(key))
          .map((key) => ({
            key,
            label: this.toTitleCase(key)
          }))
      : [];

    if (!this.editableRecord && this.suppressEmptyRecordTemplate) {
      this.formFields = [];
      return;
    }

    const configuredFields = this.resolveConfiguredFormFields(excludedColumns);
    const shouldUseConfiguredOrder = configuredFields.length > 0;

    this.formFields = shouldUseConfiguredOrder
      ? configuredFields
      : [...recordFields].sort((left, right) => left.label.localeCompare(right.label));

    if (!this.editableRecord && this.formFields.length > 0) {
      this.editableRecord = this.createEmptyRecordFromFields(this.formFields);
    } else if (this.editableRecord && this.formFields.length > 0) {
      this.formFields.forEach((field) => {
        if (!Object.prototype.hasOwnProperty.call(this.editableRecord, field.key)) {
          this.editableRecord[field.key] = null;
        }
      });
    }

    this.formFields.forEach((field) => {
      if (this.hasFieldDropdown(field.key) && !this.fieldDropdownOptions[field.key]) {
        this.onFieldDropdownOpened(field.key, true);
      }
    });
  }

  private clearSelectedRecordState(clearSchema: boolean = false): void {
    this.originalRecord = null;
    this.editableRecord = null;
    // Do not suppress the empty form: when no record is selected (e.g. the table
    // has no rows) we still want to render the entity's fields as a blank form
    // rather than collapsing to an empty/white panel. The field set is rebuilt
    // from the configured columns in generateFormFieldsFromRecord().
    this.suppressEmptyRecordTemplate = false;

    if (clearSchema) {
      this.tableSchemaFields = [];
    }

    this.generateFormFieldsFromRecord();
  }

  private resolveConfiguredFormFields(excludedColumns: Set<string>): { key: string, label: string }[] {
    const configuredColumns = Array.isArray(this.data?.columns) ? this.data.columns : [];
    const configuredFields = Array.isArray(this.data?.formFields) ? this.data.formFields : [];
    const configuredFieldOrder = Array.isArray(this.data?.fieldOrder) ? this.data.fieldOrder : [];
    const metadataFields = [
      ...Array.from(this.domainAttributeKeys),
      ...Array.from(this.freeformFieldKeys),
    ];
    const rawFields = [
      ...this.tableSchemaFields,
      ...configuredColumns,
      ...configuredFields,
      ...configuredFieldOrder,
      ...metadataFields,
    ];
    const seen = new Set<string>();

    return this.normalizeConfiguredFieldList(rawFields)
      .filter((field) => field.key !== 'id' && !excludedColumns.has(field.key))
      .filter((field) => {
        if (seen.has(field.key)) {
          return false;
        }

        seen.add(field.key);
        return true;
      });
  }

  private normalizeConfiguredFormField(field: any): { key: string, label: string } | null {
    if (typeof field === 'string') {
      const key = field.trim();
      return key ? { key, label: this.toTitleCase(key) } : null;
    }

    if (!field || typeof field !== 'object') {
      return null;
    }

    const key = String(field?.key ?? field?.field ?? field?.name ?? '').trim();
    if (!key) {
      return null;
    }

    return {
      key,
      label: String(field?.label || this.toTitleCase(key)).trim(),
    };
  }

  private normalizeConfiguredFieldList(fields: any[]): { key: string, label: string }[] {
    return (Array.isArray(fields) ? fields : [])
      .map((field) => this.normalizeConfiguredFormField(field))
      .filter((field): field is { key: string, label: string } => !!field);
  }

  private getExcludedColumns(): Set<string> {
    return new Set(
      [
        ...(Array.isArray(this.data?.excludeColumns) ? this.data.excludeColumns : []),
        ...(Array.isArray(this.data?.hiddenColumns) ? this.data.hiddenColumns : []),
        ...(Array.isArray(this.data?.excludeFields) ? this.data.excludeFields : []),
      ].map((item: any) => String(item).trim()).filter(Boolean),
    );
  }

  hasFieldDropdown(fieldKey: string): boolean {
    return !!this.getFieldDropdownConfig(fieldKey);
  }

  getDomainReferenceTooltip(fieldKey: string): string {
    const entity = this.domainAttributeEntities.get(fieldKey);
    return entity
      ? `Go to the "${entity}" entity to view attribute value details`
      : 'Reference Entity';
  }

  navigateToDomainReference(fieldKey: string): void {
    const entity = this.domainAttributeEntities.get(fieldKey);
    if (!entity) {
      return;
    }

    const entityFilterKey = this.getEntityFilterKey();
    const selectedEntityLabelFilterKey = this.getSelectedEntityLabelFilterKey();
    const selectedEntityLabel = this.toTitleCase(fieldKey);

    this.globalFilterService.updateFilterSelection(
      entityFilterKey,
      [entity],
      true,
      this.data?.widgetId,
    );
    this.globalFilterService.updateFilterSelection(
      selectedEntityLabelFilterKey,
      [selectedEntityLabel],
      true,
      this.data?.widgetId,
    );

    const queryParams: Record<string, any> = {
      [entityFilterKey]: entity,
      [selectedEntityLabelFilterKey]: selectedEntityLabel,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  onFieldDropdownOpened(fieldKey: string, isOpen = true): void {
    if (!isOpen) {
      return;
    }

    if (!fieldKey) {
      return;
    }

    const config = this.getFieldDropdownConfig(fieldKey);
    if (!config?.apiUrl) {
      return;
    }

    this.fieldDropdownLoading[fieldKey] = true;
    const payload = this.buildFieldDropdownPayload(fieldKey, config);

    this.configService.getDropdownOptions(
      config.apiUrl,
      payload,
      config.method || 'post',
      config.headers || this.data?.headers,
    ).subscribe({
      next: (response: any) => {
        this.fieldDropdownOptions[fieldKey] = this.normalizeFieldDropdownOptions(response, fieldKey);
        this.fieldDropdownLoading[fieldKey] = false;
      },
      error: () => {
        this.fieldDropdownOptions[fieldKey] = [];
        this.fieldDropdownLoading[fieldKey] = false;
      }
    });
  }

  onDomainSelectOpened(fieldKey: string, isOpen: boolean): void {
    if (isOpen) {
      this.setFieldDropdownSearchTerm(fieldKey, '');
      this.focusDomainDropdownSearch();
    }
    this.onFieldDropdownOpened(fieldKey, isOpen);
  }

  private focusDomainDropdownSearch(): void {
    setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>('.side-entity-domain-select-panel .domain-search-input');
      input?.focus();
    }, 0);
  }

  formatFieldValue(value: any): string {
    return this.formatObjectLabel(value);
  }

  getFieldDisplayLabel(fieldKey: string): string {
    const value = this.editableRecord?.[fieldKey];
    const dropdown = this.getFieldDropdownConfig(fieldKey);

    if (!dropdown) {
      return this.formatFieldValue(value);
    }

    if (value === null || value === undefined || value === '') {
      return '--Select--';
    }

    const selectedValue = this.getFieldSelectedValue(value, dropdown);
    const selectedOption = (this.fieldDropdownOptions[fieldKey] || []).find((option: any) =>
      this.compareFieldDropdownValues(this.getFieldDropdownValue(option), selectedValue)
    );

    if (selectedOption) {
      return this.getFieldDropdownLabel(selectedOption);
    }

    return this.formatFieldValue(value);
  }

  getFieldDropdownSearchTerm(fieldKey: string): string {
    return this.fieldDropdownSearchTerms[fieldKey] || '';
  }

  setFieldDropdownSearchTerm(fieldKey: string, searchTerm: string): void {
    this.fieldDropdownSearchTerms[fieldKey] = String(searchTerm || '');
  }

  getFilteredFieldDropdownOptions(fieldKey: string): any[] {
    const options = this.fieldDropdownOptions[fieldKey] || [];
    const searchTerm = this.getFieldDropdownSearchTerm(fieldKey).trim().toLowerCase();
    if (!searchTerm) {
      return options;
    }

    return options.filter((option) => {
      const searchableText = [
        this.getFieldDropdownLabel(option),
        this.getFieldDropdownValue(option),
        option?.label,
        option?.value,
        option?.data,
      ]
        .map((value) => this.stringifyDropdownSearchValue(value))
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(searchTerm);
    });
  }

  compareFieldDropdownValues = (a: any, b: any): boolean => {
    return this.getComparableValue(a) === this.getComparableValue(b);
  };

  getFieldSelectedValue(value: any, dropdownConfig?: any): any {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value !== 'object') {
      return value;
    }

    const data = value?.data ?? value;
    const explicitValueKeys = [
      dropdownConfig?.dropdownValueKey,
      dropdownConfig?.valueKey,
      dropdownConfig?.idKey,
    ]
      .map((key) => String(key || '').trim())
      .filter(Boolean);

    for (const key of explicitValueKeys) {
      const resolved = data?.[key] ?? value?.[key];
      if (resolved !== undefined && resolved !== null && resolved !== '') {
        return resolved;
      }
    }

    return (
      data?.id ??
      data?.code ??
      data?.value ??
      value?.id ??
      value?.code ??
      value?.value ??
      value
    );
  }

  getFieldDropdownLabel(option: any): string {
    return String(option?.label ?? this.formatObjectLabel(option));
  }

  private stringifyDropdownSearchValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value !== 'object') {
      return String(value);
    }

    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }

  getFieldDropdownValue(option: any): any {
    return option?.value ?? this.resolveDropdownValue(option) ?? option?.data ?? option;
  }

  onFieldValueChange(fieldKey: string, value: any): void {
    this.editableRecord[fieldKey] = value;
  }

  clearFieldDropdown(fieldKey: string): void {
    this.onFieldValueChange(fieldKey, null);
    this.setFieldDropdownSearchTerm(fieldKey, '');
  }

  private normalizeValueForSave(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === 'string') {
      return value.trim() === '' ? null : value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeValueForSave(item));
    }

    if (typeof value !== 'object') {
      return value;
    }

    const data = value?.data ?? value;
    const resolved =
      data?.id ??
      value?.id ??
      data?.uuid ??
      value?.uuid ??
      data?.code ??
      value?.code ??
      data?.value ??
      value?.value ??
      data?.name ??
      value?.name;

    if (resolved !== undefined && resolved !== null) {
      return resolved;
    }

    const normalizedObject: Record<string, any> = {};
    Object.keys(data || {}).forEach((key) => {
      if (key !== 'data') {
        normalizedObject[key] = this.normalizeValueForSave(data[key]);
      }
    });
    return normalizedObject;
  }

  private valuesEqualForSave(left: any, right: any): boolean {
    return JSON.stringify(this.normalizeValueForSave(left)) === JSON.stringify(this.normalizeValueForSave(right));
  }

  private buildSaveValue(isNewRecord: boolean, changedFields: Record<string, any> | null): Record<string, any> {
    if (isNewRecord) {
      const normalizedRecord = this.normalizeValueForSave(this.editableRecord);
      return normalizedRecord && typeof normalizedRecord === 'object' ? normalizedRecord : {};
    }

    const identifier =
      this.originalRecord?.uuid ??
      this.editableRecord?.uuid ??
      this.originalRecord?.id ??
      this.editableRecord?.id ??
      null;

    return {
      ...(identifier !== null && identifier !== undefined ? { uuid: identifier } : {}),
      ...(changedFields || {}),
    };
  }

  private buildConfiguredSavePayload(action: any, isNewRecord: boolean, changedFields: Record<string, any> | null): any | null {
    const template = action?.savePayload || action?.customPayload || action?.rawPayload;
    if (!template) {
      return null;
    }

    const payload = JSON.parse(JSON.stringify(template));
    const requestDataValue = this.buildSaveValue(isNewRecord, changedFields);
    payload.requestData = {
      ...(payload.requestData || {}),
      ...(requestDataValue && typeof requestDataValue === 'object' ? requestDataValue : {}),
    };
    if (payload?.requestData && Object.prototype.hasOwnProperty.call(payload.requestData, 'value')) {
      delete payload.requestData.value;
    }

    payload.functionName = payload.functionName || action?.functionName || this.data?.functionName || 'GenericSaveService';
    payload.entity = payload.entity || this.data?.entity || '';
    payload.workflowId = payload.workflowId || this.data?.workflowId || '';
    payload.communicationType = payload.communicationType || 'rest';
    payload.version = payload.version || action?.version || this.data?.version || '1.0';

    if (!Array.isArray(payload.endpoints) || payload.endpoints.length === 0) {
      payload.endpoints = [
        {
          functionName: payload.functionName,
          version: payload.version,
        },
      ];
    } else {
      payload.endpoints = payload.endpoints.map((endpoint: any, index: number) => {
        if (index !== 0 || !endpoint || typeof endpoint !== 'object') {
          return endpoint;
        }

        return {
          ...endpoint,
          functionName: endpoint.functionName || payload.functionName,
          version: endpoint.version || payload.version,
        };
      });
    }

    return payload;
  }

  private getFieldDropdownConfig(fieldKey: string): any {
    const explicitConfig =
      this.data?.fieldDropdowns?.[fieldKey] ||
      this.data?.fieldDropdownConfig?.[fieldKey];

    if (explicitConfig) {
      return explicitConfig;
    }

    if (!this.domainAttributeKeys.has(fieldKey)) {
      return null;
    }

    return this.buildDynamicFieldDropdownConfig(fieldKey);
  }

  private buildDynamicFieldDropdownConfig(fieldKey: string): any {
    const functionName =
      this.data?.fieldDropdownFunctionName ||
      this.data?.attributeValuesFunctionName ||
      'GenericAttributeValuesService';
    const version = this.data?.version || '1.0';

    return {
      apiUrl:
        this.data?.fieldDropdownApiUrl ||
        this.data?.attributeValuesApiUrl ||
        this.data?.apiUrl ||
        this.data?.domainAttributeApiUrl ||
        this.data?.domainAttributeapiUrl ||
        this.data?.domainAttributesApiUrl,
      method: this.data?.fieldDropdownMethod || 'post',
      requestData: {
        entity: '',
        attribute: fieldKey,
      },
      customPayload: {
        requestData: {
          entity: '',
          attribute: fieldKey,
        },
        responseList: [],
        entity: this.data?.entity || '',
        workflowId: this.data?.workflowId || '',
        endpoints: [
          {
            functionName,
            version,
          },
        ],
        communicationType: this.data?.communicationType || 'rest',
        version,
      },
      functionName,
      version,
    };
  }

  private loadDomainAttributesForEntity(entity: any): void {
    const entityKey = this.normalizeFilterValue(entity);
    if (!entityKey || !this.getDomainAttributeApiUrl()) {
      this.domainAttributeKeys.clear();
      this.freeformFieldKeys.clear();
      this.domainAttributeEntities.clear();
      this.loadedDomainAttributesEntity = null;
      this.clearInferredDropdownOptions();
      this.generateFormFieldsFromRecord();
      return;
    }

    const normalizedEntity = String(entityKey).trim();
    if (!normalizedEntity || normalizedEntity === this.loadedDomainAttributesEntity) {
      return;
    }

    this.domainAttributesSubscription?.unsubscribe();
    this.domainAttributeKeys.clear();
    this.freeformFieldKeys.clear();
    this.domainAttributeEntities.clear();
    this.loadedDomainAttributesEntity = normalizedEntity;
    this.clearInferredDropdownOptions();

    const payload = this.buildDomainAttributesPayload(normalizedEntity);
    this.domainAttributesSubscription = this.configService.getDropdownOptions(
      this.getDomainAttributeApiUrl(),
      payload,
      this.data?.domainAttributeMethod || this.data?.domainAttributesMethod || 'post',
      this.data?.domainAttributeHeaders || this.data?.headers,
    ).subscribe({
      next: (response: any) => {
        this.domainAttributeKeys = this.extractDomainAttributeKeys(response);
        this.freeformFieldKeys = this.extractFreeformFieldKeys(response);
        this.generateFormFieldsFromRecord();
        this.primeDomainAttributeDropdowns();
        this.cdr.detectChanges();
      },
      error: () => {
        this.domainAttributeKeys.clear();
        this.freeformFieldKeys.clear();
        this.domainAttributeEntities.clear();
        this.clearInferredDropdownOptions();
        this.generateFormFieldsFromRecord();
        this.cdr.detectChanges();
      },
    });
  }

  private getDomainAttributeApiUrl(): string {
    return String(
      this.data?.domainAttributeApiUrl ||
      this.data?.domainAttributeapiUrl ||
      this.data?.domainAttributesApiUrl ||
      this.data?.attributeMetadataApiUrl ||
      '',
    ).trim();
  }

  private buildDomainAttributesPayload(entity: string): any {
    const selectedEntity = this.resolveSelectedEntityForDomainAttributes(entity);
    const template =
      this.data?.domainAttributePayload ||
      this.data?.domainAttributesPayload ||
      this.data?.attributeMetadataPayload;
    const payload = template
      ? JSON.parse(JSON.stringify(template))
      : {
          requestData: {},
          responseList: [],
          entity: this.data?.entity || '',
          workflowId: this.data?.workflowId || '',
          endpoints: [],
          communicationType: this.data?.communicationType || 'rest',
          version: this.data?.version || '1.0',
        };

    payload.requestData = {
      ...(payload.requestData || {}),
      ...(this.data?.domainAttributeRequestData || {}),
      entity: selectedEntity,
    };

    const functionName =
      this.data?.domainAttributeFunctionName ||
      this.data?.domainAttributesFunctionName ||
      this.data?.attributeMetadataFunctionName ||
      'GenericEntityAttributesService';
    const version = this.data?.domainAttributeVersion || this.data?.version || '1.0';

    payload.functionName = payload.functionName || functionName;
    payload.entity = payload.entity || this.data?.entity || '';
    payload.workflowId = payload.workflowId || this.data?.workflowId || '';
    payload.communicationType = payload.communicationType || this.data?.communicationType || 'rest';
    payload.version = payload.version || version;

    if (!Array.isArray(payload.endpoints) || payload.endpoints.length === 0) {
      payload.endpoints = [{ functionName, version }];
    } else {
      payload.endpoints = payload.endpoints.map((endpoint: any, index: number) => {
        if (index !== 0 || !endpoint || typeof endpoint !== 'object') {
          return endpoint;
        }

        return {
          ...endpoint,
          functionName: endpoint.functionName || functionName,
          version: endpoint.version || version,
        };
      });
    }

    return payload;
  }

  private resolveSelectedEntityForDomainAttributes(fallbackEntity: any): string {
    const currentFilters = this.globalFilterService.getCurrentFilters?.();
    const entityFilterKey = this.getEntityFilterKey();
    const selectedEntity = this.getFirstValue(
      fallbackEntity,
      this.selectedEntityValue,
      currentFilters?.requestParams?.[entityFilterKey],
      currentFilters?.queryParams?.[entityFilterKey],
    );

    return String(selectedEntity || '').trim();
  }

  private extractDomainAttributeKeys(response: any): Set<string> {
    const domainAttributes = this.findDomainAttributes(response, new WeakSet<object>());
    const keys = new Set<string>();
    this.domainAttributeEntities.clear();

    domainAttributes.forEach((item: any) => {
      const key = this.getDomainAttributeKey(item);
      if (key) {
        keys.add(key);
        const entity = this.getDomainAttributeEntity(item);
        if (entity) {
          this.domainAttributeEntities.set(key, entity);
        }
      }
    });

    return keys;
  }

  private extractFreeformFieldKeys(response: any): Set<string> {
    const freeformFields = this.findFreeformFields(response, new WeakSet<object>());
    return new Set(
      freeformFields
        .map((item: any) => String(item || '').trim())
        .filter(Boolean),
    );
  }

  private findDomainAttributes(value: any, seen: WeakSet<object>): any[] {
    if (!value || typeof value !== 'object') {
      return [];
    }

    if (seen.has(value)) {
      return [];
    }
    seen.add(value);

    if (Array.isArray(value)) {
      for (const item of value) {
        if (this.looksLikeDomainAttributeEntry(item)) {
          return value;
        }

        const nested = this.findDomainAttributes(item, seen);
        if (nested.length) {
          return nested;
        }
      }
      return [];
    }

    const direct =
      value?.domainAttributes ??
      value?.domain_attributes ??
      value?.attributesList ??
      value?.attributes_list ??
      value?.attributes ??
      value?.fields;
    if (Array.isArray(direct)) {
      return direct;
    }

    for (const nestedValue of Object.values(value)) {
      const nested = this.findDomainAttributes(nestedValue, seen);
      if (nested.length) {
        return nested;
      }
    }

    return [];
  }

  private looksLikeDomainAttributeEntry(value: any): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    return !!(
      value?.attribute ||
      value?.attributeName ||
      value?.field ||
      value?.fieldKey ||
      value?.key ||
      value?.name
    );
  }

  private findFreeformFields(value: any, seen: WeakSet<object>): any[] {
    if (!value || typeof value !== 'object') {
      return [];
    }

    if (seen.has(value)) {
      return [];
    }
    seen.add(value);

    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim()) {
          return value;
        }

        const nested = this.findFreeformFields(item, seen);
        if (nested.length) {
          return nested;
        }
      }
      return [];
    }

    const direct =
      value?.freeform ??
      value?.freeForm ??
      value?.freeformFields ??
      value?.free_form ??
      value?.freeFormFields ??
      value?.free_form_fields;
    if (Array.isArray(direct)) {
      return direct;
    }

    for (const nestedValue of Object.values(value)) {
      const nested = this.findFreeformFields(nestedValue, seen);
      if (nested.length) {
        return nested;
      }
    }

    return [];
  }

  private getDomainAttributeKey(item: any): string {
    if (item === null || item === undefined) {
      return '';
    }

    if (typeof item !== 'object') {
      return String(item).trim();
    }

    return String(
      item?.attribute ||
      item?.attributeName ||
      item?.field ||
      item?.fieldKey ||
      item?.key ||
      item?.name ||
      '',
    ).trim();
  }

  private getDomainAttributeEntity(item: any): string {
    if (!item || typeof item !== 'object') {
      return '';
    }

    return String(
      item?.entity ||
      item?.referenceEntity ||
      item?.domainEntity ||
      item?.entityName ||
      item?.reference_entity ||
      item?.domain_entity ||
      '',
    ).trim();
  }

  private clearInferredDropdownOptions(): void {
    Object.keys(this.fieldDropdownOptions || {}).forEach((fieldKey) => {
      if (!this.data?.fieldDropdowns?.[fieldKey] && !this.data?.fieldDropdownConfig?.[fieldKey]) {
        delete this.fieldDropdownOptions[fieldKey];
      }
    });
  }

  private primeDomainAttributeDropdowns(): void {
    this.formFields.forEach((field) => {
      if (this.domainAttributeKeys.has(field.key) && !this.fieldDropdownOptions[field.key]) {
        this.onFieldDropdownOpened(field.key, true);
      }
    });
  }

  private buildFieldDropdownPayload(fieldKey: string, config: any): any {
    const payload = JSON.parse(JSON.stringify(config?.customPayload || {}));
    const resolvedEntity = this.resolveSelectedEntityForDropdown(fieldKey);
    payload.requestData = {
      ...(payload.requestData || {}),
      ...(config?.requestData || {}),
    };

    if (!String(payload.requestData.attribute ?? '').trim()) {
      payload.requestData.attribute = fieldKey;
    }

    if (resolvedEntity) {
      payload.requestData.entity = resolvedEntity;
    }

    if (resolvedEntity) {
      payload.entity = resolvedEntity;
    }

    if (!payload.endpoints || !Array.isArray(payload.endpoints) || payload.endpoints.length === 0) {
      payload.endpoints = [
        {
          functionName: config?.functionName || this.data?.functionName || 'GenericAttributeValuesService',
          version: config?.version || this.data?.version || '1.0',
        },
      ];
    }

    payload.functionName = payload.functionName || config?.functionName || this.data?.functionName || 'GenericAttributeValuesService';
    payload.entity = resolvedEntity || payload.entity || this.data?.entity || '';
    payload.workflowId = payload.workflowId || this.data?.workflowId || '';
    payload.communicationType = payload.communicationType || 'rest';
    payload.version = payload.version || config?.version || this.data?.version || '1.0';

    return payload;
  }

  private resolveSelectedEntityForDropdown(fieldKey: string): string | null {
    const currentFilters = this.globalFilterService.getCurrentFilters?.();
    const entityFilterKey = this.getEntityFilterKey();
    const selectedEntityLabelFilterKey = this.getSelectedEntityLabelFilterKey();
    const filterEntity = currentFilters?.queryParams?.[entityFilterKey] || currentFilters?.requestParams?.[entityFilterKey];
    const normalizedFilterEntity = this.normalizeFilterValue(filterEntity);
    const selectedEntityLabel =
      currentFilters?.queryParams?.[selectedEntityLabelFilterKey] ||
      currentFilters?.requestParams?.[selectedEntityLabelFilterKey];
    const normalizedSelectedEntityLabel = this.normalizeFilterValue(selectedEntityLabel);

    const directCandidates = [
      this.selectedEntityValue,
      normalizedFilterEntity,
      normalizedSelectedEntityLabel,
      this.data?.requestData?.entity,
      this.data?.entity,
      this.data?.entityName,
      this.editableRecord?.entity,
      this.originalRecord?.entity,
      this.data?.record?.entity,
    ];

    for (const candidate of directCandidates) {
      if (candidate !== null && candidate !== undefined && String(candidate).trim()) {
        return String(candidate);
      }
    }

    const searchRoots = [this.editableRecord, this.originalRecord, this.data];
    for (const root of searchRoots) {
      const resolved = this.findEntityForField(root, fieldKey, new WeakSet<object>());
      if (resolved) {
        return resolved;
      }
    }
    return null;
  }

  private normalizeFilterValue(value: any): any {
    if (Array.isArray(value)) {
      return value.length > 0 ? value[0] : null;
    }

    return value;
  }

  private getFirstValue(...values: any[]): any {
    for (const value of values) {
      const normalized = this.normalizeFilterValue(value);
      if (normalized !== null && normalized !== undefined && String(normalized).trim()) {
        return normalized;
      }
    }
    return null;
  }

  private filterKeyMatches(changedFilterKey: any, expectedKey: string): boolean {
    if (Array.isArray(changedFilterKey)) {
      return changedFilterKey.some((key: any) => String(key) === expectedKey);
    }
    return String(changedFilterKey) === expectedKey;
  }

  private getEntityFilterKey(): string {
    return String(this.data?.entityFilterKey || this.data?.entityKey || 'entity');
  }

  private getSelectedEntityLabelFilterKey(): string {
    return String(this.data?.selectedEntityLabelFilterKey || this.data?.entityLabelFilterKey || 'selected_entity_label');
  }

  private getColumnSchemaFilterKeys(): string[] {
    const listeners = Array.isArray(this.data?.filterListner)
      ? this.data.filterListner
      : Array.isArray(this.data?.filterListener)
        ? this.data.filterListener
        : [];

    return listeners
      .map((listener: any) => `${String(listener || '').trim()}_columns`)
      .filter((key: string) => !!key && key !== '_columns');
  }

  private deriveSectionTitleFromRecord(record: any): string | null {
    if (record === null || record === undefined || typeof record !== 'object') {
      return null;
    }

    const candidates = [
      record?.[this.getSelectedEntityLabelFilterKey()],
      record?.selectedEntityLabel,
      record?.entityLabel,
      record?.label,
      record?.name,
      record?.display_name,
      record?.displayName,
      record?.entity,
      record?.id,
      record?.uuid,
    ];

    return this.getFirstValue(...candidates);
  }

  private getCurrentSelectedEntityTitle(): string | null {
    const currentFilters = this.globalFilterService.getCurrentFilters?.();
    const entityFilterKey = this.getEntityFilterKey();
    const selectedEntityLabelFilterKey = this.getSelectedEntityLabelFilterKey();
    return this.getFirstValue(
      currentFilters?.queryParams?.[selectedEntityLabelFilterKey],
      currentFilters?.requestParams?.[selectedEntityLabelFilterKey],
      currentFilters?.queryParams?.[entityFilterKey],
      currentFilters?.requestParams?.[entityFilterKey],
    );
  }

  private findEntityForField(value: any, fieldKey: string, seen: WeakSet<object>): string | null {
    if (value === null || value === undefined || typeof value !== 'object') {
      return null;
    }

    if (seen.has(value)) {
      return null;
    }
    seen.add(value);

    const normalizedFieldKey = String(fieldKey || '').trim();

    if (Array.isArray(value)) {
      for (const item of value) {
        const resolved = this.findEntityForField(item, fieldKey, seen);
        if (resolved) {
          return resolved;
        }
      }
      return null;
    }

    const candidateAttribute =
      value?.attribute ??
      value?.attributeName ??
      value?.key ??
      value?.name;
    const candidateEntity = value?.entity;

    if (
      candidateEntity !== undefined &&
      candidateEntity !== null &&
      String(candidateAttribute || '').trim() === normalizedFieldKey
    ) {
      return String(candidateEntity);
    }

    for (const nestedValue of Object.values(value)) {
      const resolved = this.findEntityForField(nestedValue, fieldKey, seen);
      if (resolved) {
        return resolved;
      }
    }

    return null;
  }

  private normalizeFieldDropdownOptions(response: any, fieldKey: string): any[] {
    const dropdownConfig = this.getFieldDropdownConfig(fieldKey);
    const rawRows =
      response?.responseList?.[0]?.[fieldKey] ||
      response?.responseList?.[0]?.data?.[0]?.[fieldKey] ||
      response?.responseList?.[0]?.data?.[fieldKey] ||
      response?.responseList?.[0]?.data?.[0]?.data ||
      response?.responseList?.[0]?.data ||
      response?.data?.[0]?.data ||
      response?.data ||
      response?.options ||
      [];

    const rows = Array.isArray(rawRows) ? rawRows : [];
    const seen = new Set<string>();
    const options: any[] = [];

    rows.forEach((row: any) => {
      const normalized = this.normalizeDropdownOption(row, dropdownConfig);
      if (!normalized) {
        return;
      }

      const dedupeKey = this.getComparableValue(normalized.value);
      if (seen.has(dedupeKey)) {
        return;
      }
      seen.add(dedupeKey);
      options.push(normalized);
    });

    return options;
  }

  private normalizeDropdownOption(option: any, dropdownConfig?: any): { label: string; value: any } | null {
    if (option === null || option === undefined) {
      return null;
    }

    if (typeof option !== 'object') {
      const text = String(option);
      return {
        label: text,
        value: option,
      };
    }

    const data = option?.data ?? option;
    const value = this.resolveDropdownValue(option, dropdownConfig);
    const label = this.resolveDropdownLabel(option, dropdownConfig);

    if (label === undefined || value === undefined) {
      return null;
    }

    return {
      label: String(label),
      value,
    };
  }

  private formatObjectLabel(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value !== 'object') {
      return String(value);
    }

    const data = value?.data ?? value;
    const code = data?.code ?? data?.id ?? value?.code ?? value?.id;
    const name = data?.name ?? data?.display_name ?? data?.displayName ?? value?.name ?? value?.display_name ?? value?.displayName;

    if (code !== undefined && name !== undefined && code !== null && name !== null) {
      return `${code} - ${name}`;
    }
    if (name !== undefined && name !== null) {
      return String(name);
    }
    if (code !== undefined && code !== null) {
      return String(code);
    }
    return JSON.stringify(value);
  }

  private getComparableValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value !== 'object') {
      return String(value);
    }

    const data = value?.data ?? value;
    return String(data?.id ?? data?.code ?? value?.id ?? value?.code ?? value?.name ?? JSON.stringify(value));
  }

  private resolveDropdownLabel(option: any, dropdownConfig?: any): string | undefined {
    const data = option?.data ?? option;
    const explicitLabelKeys = [
      dropdownConfig?.dropdownLabelKey,
      dropdownConfig?.labelKey,
      dropdownConfig?.displayKey,
      dropdownConfig?.textKey,
    ]
      .map((key) => String(key || '').trim())
      .filter(Boolean);

    for (const key of explicitLabelKeys) {
      const resolved = this.resolveValueByKey(data, option, key);
      if (resolved !== undefined && resolved !== null && resolved !== '') {
        return String(resolved);
      }
    }

    const preferredLabel =
      this.resolveValueByKey(data, option, 'code') ??
      this.resolveValueByKey(data, option, 'id') ??
      this.resolveValueByKey(data, option, 'label') ??
      this.resolveValueByKey(data, option, 'display_name') ??
      this.resolveValueByKey(data, option, 'displayName') ??
      this.resolveValueByKey(data, option, 'name') ??
      this.resolveValueByKey(data, option, 'value');

    return preferredLabel !== undefined && preferredLabel !== null ? String(preferredLabel) : undefined;
  }

  private resolveDropdownValue(option: any, dropdownConfig?: any): any {
    const data = option?.data ?? option;
    const explicitValueKeys = [
      dropdownConfig?.dropdownValueKey,
      dropdownConfig?.valueKey,
      dropdownConfig?.idKey,
    ]
      .map((key) => String(key || '').trim())
      .filter(Boolean);

    for (const key of explicitValueKeys) {
      const resolved = this.resolveValueByKey(data, option, key);
      if (resolved !== undefined && resolved !== null && resolved !== '') {
        return resolved;
      }
    }

    return (
      this.resolveValueByKey(data, option, 'code') ??
      this.resolveValueByKey(data, option, 'id') ??
      this.resolveValueByKey(data, option, 'value') ??
      this.resolveValueByKey(data, option, 'name') ??
      option
    );
  }

  private resolveValueByKey(primary: any, fallback: any, key: string): any {
    if (!key) {
      return undefined;
    }

    const normalizedKey = String(key).trim();
    if (!normalizedKey) {
      return undefined;
    }

    const directValue = primary?.[normalizedKey];
    if (directValue !== undefined) {
      return directValue;
    }

    return fallback?.[normalizedKey];
  }

  protected toTitleCase(str: string): string {
    // This helper function converts snake_case and camelCase to Title Case
    str = str.replace(/_/g, ' ');
    str = str.replace(/([a-z])([A-Z])/g, ' $2');
    return str
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
      .replace(/\bHp\b/g, 'HP');
  }

  isDateField(fieldKey: string): boolean {
    return /date|_dt\b/i.test(fieldKey);
  }

  getDateValue(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

    if (typeof value === 'string') {
      const dateMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const year = Number(dateMatch[1]);
        const monthIndex = Number(dateMatch[2]) - 1;
        const day = Number(dateMatch[3]);
        const localDate = new Date(year, monthIndex, day);
        return isNaN(localDate.getTime()) ? null : localDate;
      }
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  onDateValueChange(fieldKey: string, date: Date | null): void {
    if (!date) {
      this.onFieldValueChange(fieldKey, null);
      return;
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const timeSuffix = this.getDateTimeSuffixForField(fieldKey, date);
    this.onFieldValueChange(fieldKey, `${y}-${m}-${d}${timeSuffix}`);
  }

  private getDateTimeSuffixForField(fieldKey: string, selectedDate: Date): string {
    return this.extractDateTimeSuffix(this.editableRecord?.[fieldKey]) ||
      this.extractDateTimeSuffix(this.originalRecord?.[fieldKey]) ||
      this.formatDateTimeSuffix(selectedDate);
  }

  private extractDateTimeSuffix(value: any): string {
    if (!value) {
      return '';
    }

    if (value instanceof Date) {
      return this.formatDateTimeSuffix(value);
    }

    if (typeof value !== 'string') {
      return '';
    }

    const normalized = value.trim();
    const suffixMatch = normalized.match(/^\d{4}-\d{2}-\d{2}([T\s].+)$/);
    if (suffixMatch?.[1]) {
      return suffixMatch[1];
    }

    return '';
  }

  private formatDateTimeSuffix(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const microseconds = String(date.getMilliseconds() * 1000).padStart(6, '0');
    return `T${hours}:${minutes}:${seconds}.${microseconds}Z`;
  }

  ngOnDestroy(): void {
    if (this.recordFilterSubscription) {
      this.recordFilterSubscription.unsubscribe();
    }
    if (this.dropdownFilterSubscription) {
      this.dropdownFilterSubscription.unsubscribe();
    }
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
    this.domainAttributesSubscription?.unsubscribe();
    this.reflowSub?.unsubscribe();
  }

  resetForm(): void {
    if (this.originalRecord) {
      this.editableRecord = this.cloneRecord(this.originalRecord); // Reset from original
    } else if (this.formFields.length > 0) {
      this.editableRecord = this.createEmptyRecordFromFields(this.formFields);
    } else {
      this.editableRecord = null;
    }
  }

  saveForm(action: any): void {
    if (!action.apiUrl) {
      console.error('Save API URL is not defined.');
      return;
    }

    this.isSaving = true;
    const method = action.method || 'POST';
    const configuredSaveTemplate = action?.savePayload || action?.customPayload || action?.rawPayload;

    // Logic to check for changes or if it's a new record
    const isNewRecord = (this.originalRecord === null || this.originalRecord === undefined || this.originalRecord.id === null);

    const primaryId =
      this.originalRecord?.Code ??
      this.originalRecord?.code ??
      this.editableRecord?.Code ??
      this.editableRecord?.code ??
      null;

    if (!configuredSaveTemplate && !isNewRecord && (primaryId === null || primaryId === undefined || primaryId === '')) {
      this.isSaving = false;
      this.snackBar.open('Cannot save: primaryId (Code) is missing.', 'Close', { duration: 10000 });
      return;
    }

    const buildChangedFields = (editable: any, original: any): Record<string, any> => {
      const changed: Record<string, any> = {};
      const editableKeys = Object.keys(editable || {});
      for (const key of editableKeys) {
        // if (key === 'id') continue;
        if (!this.valuesEqualForSave(editable?.[key], original?.[key])) {
          changed[key] = this.normalizeValueForSave(editable?.[key]);
        }
      }
      return changed;
    };

    const changedFields = isNewRecord ? null : buildChangedFields(this.editableRecord, this.originalRecord);
    let hasChanges = false;

    if (!isNewRecord) {
      hasChanges = !!changedFields && Object.keys(changedFields).length > 0;
    } else {
      hasChanges = this.hasMeaningfulValues(this.editableRecord);
    }

    if (!hasChanges) {
      this.isSaving = false;
      this.snackBar.open('No changes to save.', 'Close', { duration: 10000 });
      return;
    }

    const dataForCreate = { ...(this.editableRecord || {}) };
    delete (dataForCreate as any).id;

    const configuredSavePayload = this.buildConfiguredSavePayload(action, isNewRecord, changedFields);
    const payload = configuredSavePayload || (isNewRecord
      ? { data: dataForCreate }
      : { primaryId, data: changedFields });

    const globalFilters = this.globalFilterService.getCurrentFilters();
    const queryParams = { ...(globalFilters?.queryParams || {}) };
    const entityFilterKey = this.getEntityFilterKey();
    if (!configuredSavePayload && !queryParams[entityFilterKey]) {
      const entityFromRequestParams = globalFilters?.requestParams?.[entityFilterKey];
      if (entityFromRequestParams) queryParams[entityFilterKey] = entityFromRequestParams;
    }

    const filtersToSend = configuredSavePayload ? {} : { queryParams, requestParams: payload };
    const requestConfig = configuredSavePayload
      ? { ...this.data, ...action, rawPayload: payload, skipDefaultPayload: true, embedQueryParams: false, embeddedParams: [], useQueryParam: false }
      : { ...this.data, ...action, skipDefaultPayload: true };

    console.log('Saving data:', payload);
    const apiUrl = action.apiUrl;

    this.configService.getApi(apiUrl, filtersToSend, requestConfig, method).subscribe({
      next: (response) => {
        this.isSaving = false;

        const statusValue = typeof response?.status === 'string' ? response.status.toLowerCase() : null;
        const isSuccess = statusValue ? statusValue === 'success' : true;
        if (!isSuccess) {
          const message =
            response?.ui_response ||
            response?.error ||
            response?.detail ||
            'Save failed.';
          console.error('Save failed', response);
          this.snackBar.open(String(message), 'Close', {
            duration: 10000,
            panelClass: ['error-snackbar']
          });
          return;
        }

        console.log('Save successful', response);
        this.snackBar.open('Saved successfully!', 'Close', {
          duration: 10000,
          panelClass: ['success-snackbar']
        });
        // After saving, update the original record to match the new state
        this.originalRecord = this.cloneRecord(this.editableRecord);
        if (action.refreshWidgets && Array.isArray(action.refreshWidgets)) {
          action.refreshWidgets.forEach((widgetId: string) => {
            this.globalFilterService.requestWidgetRefresh(widgetId);
          });
        }
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Save failed', error);
        this.snackBar.open('Error saving data.', 'Close', {
          duration: 10000,
        });
      },
    });
  }

  handleAction(actionId: string): void {
    const action = this.data?.sections
      ?.flatMap((s: any) => s.actions)
      .find((a: any) => a.actionId === actionId);

    if (action) {
      if (actionId === 'reset') {
        this.resetForm();
      } else if (actionId === 'save') {
        this.saveForm(action);
      }
    }
  }

  /**
   * Splitter between the table widget and this form panel. Dragging rewrites the
   * dashboard grid's column tracks so the form's tracks become pixel-sized and the
   * table's tracks flex to fill the remaining width.
   */
  onResizeHandlePointerDown(event: PointerEvent): void {
    const formHost = this.hostElementRef.nativeElement.closest('.grid-widget') as HTMLElement | null;
    const gridElement = formHost?.parentElement;
    if (!formHost || !gridElement) {
      return;
    }

    const gridStyles = window.getComputedStyle(gridElement);
    const totalTracks = gridStyles.gridTemplateColumns.split(' ').length;
    const spanMatch =
      /span\s+(\d+)/.exec(formHost.style.gridColumn || '') ||
      /span\s+(\d+)/.exec(window.getComputedStyle(formHost).gridColumnEnd || '');
    const formTracks = spanMatch ? parseInt(spanMatch[1], 10) : 0;
    if (formTracks <= 0 || formTracks >= totalTracks) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = formHost.getBoundingClientRect().width;
    const gridWidth = gridElement.getBoundingClientRect().width;
    const columnGap = parseFloat(gridStyles.columnGap) || 0;
    const minWidth = 280;
    const maxWidth = Math.max(minWidth, gridWidth * 0.7);

    const onPointerMove = (moveEvent: PointerEvent) => {
      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + (startX - moveEvent.clientX)));
      const trackWidth = (newWidth - (formTracks - 1) * columnGap) / formTracks;
      gridElement.style.gridTemplateColumns =
        `repeat(${totalTracks - formTracks}, minmax(0, 1fr)) repeat(${formTracks}, ${trackWidth}px)`;
    };

    const onPointerUp = () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  }

  private hasMeaningfulValues(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    if (Array.isArray(value)) {
      return value.some((item) => this.hasMeaningfulValues(item));
    }

    if (typeof value !== 'object') {
      return true;
    }

    return Object.entries(value).some(([key, nestedValue]) => key !== 'id' && this.hasMeaningfulValues(nestedValue));
  }
}
